import React, { useState } from "react";

// React Hook Form
import { useForm } from "react-hook-form";

// Zod
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";

// Supabase
import { supabase } from "../../../supabase";

// Hooks
import useSnackbarNotification from "../../../hooks/useSnackbarNotification";

const passwordSchema = z
    .object({
        currentPassword: z.string().min(1, "Current password is required"),
        newPassword: z
            .string()
            .min(6, "Password must be at least 6 characters"),
        confirmPassword: z.string().min(1, "Please confirm your password"),
    })
    .refine((data) => data.newPassword === data.confirmPassword, {
        message: "Passwords don't match",
        path: ["confirmPassword"],
    });

const ChangePassword = () => {
    const { showSuccess, showError } = useSnackbarNotification();

    const [showPassword, setShowPassword] = useState(false);
    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const {
        register,
        handleSubmit,
        formState: { errors, isSubmitting },
        reset,
    } = useForm({
        resolver: zodResolver(passwordSchema),
    });

    const onSubmit = async (data) => {
        try {
            const {
                data: { user },
            } = await supabase.auth.getUser();

            const { error: signInError } =
                await supabase.auth.signInWithPassword({
                    email: user.email,
                    password: data.currentPassword,
                });

            if (signInError) {
                showError("Current password is incorrect");
                return;
            }

            const { error } = await supabase.auth.updateUser({
                password: data.newPassword,
            });

            if (error) {
                showError(error.message);
            } else {
                showSuccess("Password updated successfully");
                reset();
            }
        } catch (error) {
            showError("An error occurred while updating your password");
        }
    };

    return (
        <>
            <h2 className="font-bold text-xl mb-4">Change password</h2>
            <form onSubmit={handleSubmit(onSubmit)} className="mb-9">
                <div className="flex gap-6">
                    <div className="flex-1 mb-4">
                        <label className="font-medium mb-1 block text-sm">
                            Current password*
                        </label>
                        <div className="relative">
                            <input
                                type={showCurrentPassword ? "text" : "password"}
                                {...register("currentPassword")}
                                className="w-full px-3 py-2 border border-gray-300 rounded-full text-sm focus:outline-none pr-10"
                            />
                            <span
                                className="absolute right-4 top-2.5 cursor-pointer select-none"
                                onClick={() =>
                                    setShowCurrentPassword((v) => !v)
                                }
                                aria-label="Toggle current password visibility"
                            >
                                {showCurrentPassword ? "🙈" : "👁️"}
                            </span>
                        </div>
                        {errors.currentPassword && (
                            <p className="text-red-500 text-xs mt-1">
                                {errors.currentPassword.message}
                            </p>
                        )}
                    </div>
                </div>

                <div className="flex flex-col md:flex-row gap-6">
                    <div className="flex-1 mb-4">
                        <label className="font-medium mb-1 block text-sm">
                            New password*
                        </label>
                        <div className="relative">
                            <input
                                type={showPassword ? "text" : "password"}
                                {...register("newPassword")}
                                className="w-full px-3 py-2 border border-gray-300 rounded-full text-sm focus:outline-none pr-10"
                            />
                            <span
                                className="absolute right-4 top-2.5 cursor-pointer select-none"
                                onClick={() => setShowPassword((v) => !v)}
                                aria-label="Toggle password visibility"
                            >
                                {showPassword ? "🙈" : "👁️"}
                            </span>
                        </div>
                        {errors.newPassword && (
                            <p className="text-red-500 text-xs mt-1">
                                {errors.newPassword.message}
                            </p>
                        )}
                    </div>
                    <div className="flex-1 mb-4">
                        <label className="font-medium mb-1 block text-sm">
                            Confirm new password*
                        </label>
                        <div className="relative">
                            <input
                                type={showConfirmPassword ? "text" : "password"}
                                {...register("confirmPassword")}
                                className="w-full px-3 py-2 border border-gray-300 rounded-full text-sm focus:outline-none pr-10"
                            />
                            <span
                                className="absolute right-4 top-2.5 cursor-pointer select-none"
                                onClick={() =>
                                    setShowConfirmPassword((v) => !v)
                                }
                                aria-label="Toggle confirm password visibility"
                            >
                                {showConfirmPassword ? "🙈" : "👁️"}
                            </span>
                        </div>
                        {errors.confirmPassword && (
                            <p className="text-red-500 text-xs mt-1">
                                {errors.confirmPassword.message}
                            </p>
                        )}
                    </div>
                </div>

                <button
                    type="submit"
                    disabled={isSubmitting}
                    className="px-8 py-2 rounded-full bg-black text-white font-medium text-base disabled:opacity-50"
                >
                    {isSubmitting ? "Updating..." : "Save"}
                </button>
            </form>
        </>
    );
};

export default ChangePassword;
